/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import numberFormat from "../common/number-format";
import defaultProfileIc from "../../assets/svg/person_ic.svg";
import "./VendorAndPendingOverviewRow.css";

const Y_LABELS = ["500", "400", "300", "200", "100", "75", "50", "20", "10", "0"];
const BREAKPOINTS = [0, 10, 20, 50, 75, 100, 200, 300, 400, 500];

const toScale9 = (value) => {
  const max = BREAKPOINTS[BREAKPOINTS.length - 1];
  const clamped = Math.min(Math.max(value, 0), max);
  for (let i = 1; i < BREAKPOINTS.length; i++) {
    if (clamped <= BREAKPOINTS[i]) {
      const lo = BREAKPOINTS[i - 1];
      const hi = BREAKPOINTS[i];
      return (i - 1) + (clamped - lo) / (hi - lo);
    }
  }
  return 9;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return (
    <div className="overdue-tooltip-card">
      <span className="overdue-tooltip-title">{data.period_label}</span>
      <div className="overdue-tooltip-body">
        <div className="overdue-tooltip-row">
          <span className="overdue-tooltip-label">Overdue</span>
          <span className="overdue-tooltip-val">
            ₹{Number(data.pending_amount).toLocaleString("en-IN")}
          </span>
        </div>
      </div>
    </div>
  );
};

const CustomCursor = (props) => {
  const { points, height } = props;
  if (!points || !points[0]) return null;
  const { x, y } = points[0];
  return (
    <g>
      <line
        x1={x}
        y1={y}
        x2={x}
        y2={y + height}
        stroke="#64748B"
        strokeWidth={1.5}
        strokeDasharray="4 4"
      />
      <circle
        cx={x}
        cy={y + height}
        r={4}
        fill="#FFFFFF"
        stroke="#212129"
        strokeWidth={2}
      />
    </g>
  );
};

export const VendorAndPendingOverviewRow = ({
  vendorsData = [],
  activeTab,
  setActiveTab,
  selectedPendingYear,
  setSelectedPendingYear,
  pendingData = [],
}) => {
  const [activePoint, setActivePoint] = useState(null);

  // Map the first 4 indices to target labels
  const MAPPED_LABELS = ["0-30 days", "31-60 days", "61-90 days", ">90 days"];
  const safePendingData = Array.isArray(pendingData) ? pendingData : [];
  const chartDataMapped = safePendingData.slice(0, 4).map((item, index) => {
    const rawVal = item.pending_amount ?? 0;
    // Scale by 1000 if raw value is raw Rupees to match 0-500 ticks
    const valueForScale = rawVal > 1000 ? rawVal / 1000 : rawVal;
    return {
      ...item,
      period_label: MAPPED_LABELS[index] || item.period_label,
      pending_amount_scaled: toScale9(valueForScale),
    };
  });

  return (
    <div className="row gx-2 mt-0">
      {/* Vendor Based Overview */}
      <div className="col-xl-6" style={{ marginTop: "16px" }}>
        <div className="vendor-card">
          <h3 className="vendor-title">
            Vendors transaction based vendor overview
          </h3>

          <div className="vendor-tabs-wrapper">
            <button
              className={`vendor-tab ${activeTab === "Top" ? "active" : "inactive"}`}
              onClick={() => setActiveTab("Top")}
            >
              Top vendors
            </button>
            <button
              className={`vendor-tab ${activeTab === "least" ? "active" : "inactive"}`}
              onClick={() => setActiveTab("least")}
            >
              Least vendors
            </button>
          </div>

          <div className="vendor-table-container">
            <div className="vendor-table-hdr">
              <span className="vendor-hdr-cell">Vendor</span>
              <span className="vendor-hdr-cell">Transaction</span>
            </div>
            <div className="vendor-table-body-list">
              {Array.isArray(vendorsData) && vendorsData.length > 0 ? (
                vendorsData.map((vendor) => (
                  <div className="vendor-table-row-item" key={vendor.vendor_id || vendor.vendor_name}>
                    <div className="vendor-cell-info">
                      {vendor.image && vendor.image.trim() !== "" ? (
                        <img
                          src={vendor.image}
                          alt="Logo"
                          className="vendor-profile-img"
                        />
                      ) : (
                        <span className="vendor-default-avatar">
                          <img src={defaultProfileIc} alt="Profile" className="vendor-default-avatar-icon" />
                        </span>
                      )}
                      <span className="vendor-name-txt" title={vendor.vendor_name}>
                        {vendor.vendor_name ?? "---"}
                      </span>
                    </div>
                    <span className="vendor-amount-txt">
                      ₹{numberFormat(vendor.amount)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="d-flex align-items-center justify-content-center textGrey fs-14 fw-500" style={{ height: "100px" }}>
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pending Amount Overview */}
      <div className="col-xl-6" style={{ marginTop: "16px" }}>
        <div className="overdue-card">
          <h3 className="overdue-title">Overdue overview</h3>

          <div className="overdue-chart-area">
            <div className="overdue-chart-plot-row">
              <div className="overdue-yaxis">
                {Y_LABELS.map((label) => (
                  <div key={label} className="overdue-yaxis-label">
                    {label}
                  </div>
                ))}
              </div>

              <div className="overdue-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartDataMapped}
                    margin={{ top: 8, right: 4, left: 10, bottom: 8 }}
                    onMouseMove={(state) => {
                      if (state && state.activeLabel) {
                        setActivePoint(state.activeLabel);
                      }
                    }}
                    onMouseLeave={() => setActivePoint(null)}
                  >
                    <CartesianGrid
                      strokeDasharray="4 4"
                      stroke="#D7DFE4"
                      strokeWidth={1}
                      vertical={false}
                    />

                    <YAxis
                      domain={[0, 9]}
                      ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]}
                      hide
                    />

                    <XAxis
                      dataKey="period_label"
                      hide
                      padding={{ left: 38, right: 38 }}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={<CustomCursor />}
                    />

                    <Line
                      type="linear"
                      dataKey="pending_amount_scaled"
                      stroke="#4F46E5"
                      strokeWidth={1.5}
                      strokeDasharray="6 6"
                      dot={false}
                      activeDot={{ r: 5, fill: "#4F46E5", stroke: "#4F46E5", strokeWidth: 1 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="overdue-xaxis">
              {chartDataMapped.map((d) => {
                const isActive = d.period_label === activePoint;
                return (
                  <div
                    key={d.period_label}
                    className={`overdue-xaxis-label ${isActive ? 'active' : ''}`}
                  >
                    {d.period_label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
